import type { HidChannel } from '../../../channels/hid/HidChannel'
import { DeviceManifest, InputReportCapability } from '../../../types/manifest'
import { BaseDevice } from '../../../runtime/BaseDevice'
import { VT_NRF54L_BASE_PROFILE } from '../../../chips/nordic-nrf54l/protocols/base-command'
import { installBaseDockFeatures } from '../support/composition/installBaseDockFeatures'
import { BaseChildReportService } from '../support/services/BaseChildReportService'
import type { BaseConnectedMouse } from '../../../types/base'

/** ESPR/VPR 等使用 Nordic nRF54L HID 协议族的底座驱动。 */
export class NordicNrf54LBaseDevice extends BaseDevice {
  /** 当前底座型号装配的 Nordic nRF54L 底座芯片能力。 */
  private readonly baseChipProfile = VT_NRF54L_BASE_PROFILE
  private readonly childReports = new BaseChildReportService(this, this.baseChipProfile)
  /** 周期性探测子鼠标的定时器，用于补齐没有主动上报时的状态。 */
  // private childPollTimer: ReturnType<typeof setTimeout> | null = null

  /** 装配 Nordic 底座能力，并把屏幕上传等专用通道解析留在底座驱动内部。 */
  constructor(channel: HidChannel, manifest: DeviceManifest) {
    super(channel, manifest)
    installBaseDockFeatures({
      device: this,
      profile: this.baseChipProfile,
      screenChannelResolver: (usagePage, usage) => this.createScreenUploadChannel(usagePage, usage),
    })
  }

  /** 底座物理连接后启动子鼠标探测，补齐历史配对但没有主动事件的场景。 */
  protected onConnected(): void {
    console.log(`[NordicNrf54LBaseDevice] Connected: ${this.manifest.model}`)
    // this.startChildPolling()
  }

  /** 断开时释放子鼠标探测定时器。 */
  protected onDisconnected(): void {
    // this.stopChildPolling()
    this.childReports.removeCurrentMouse()
  }

  /**
   * 主动透传读取当前配对子鼠标 VID/PID 并同步逻辑子设备。
   * 供有线鼠标拔出后一次性补挂 2.4G 子鼠标；不要做成周期轮询（会干扰固件升级）。
   */
  public emitCurrentConnectedMouse(): Promise<void> {
    return this.childReports.emitCurrentConnectedMouse()
  }

  /** 测试页手动读取 VID/PID 后，可主动同步底座逻辑子鼠标。 */
  public syncConnectedMouse(mouse: BaseConnectedMouse): Promise<BaseDevice | null> {
    return this.childReports.syncConnectedMouse(mouse)
  }

  /** 处理 VT 底座 input report，0x07 才是底座子鼠标事件通道。 */
  protected handleReport(reportId: number, data: DataView): void {
    if (reportId !== 0x07) {
      super.handleReport(reportId, data)
      return
    }

    const reportCapability = this.manifest.capabilities.find(
      (item): item is InputReportCapability => item.type === 'input-report',
    )
    if (reportCapability?.config.type === 'retail') {
      if (data.byteLength === 0) return

      const conProtocol = data.getUint8(0) & 0x0f
      if (conProtocol === 0) {
        this.childReports.emitRawMouseReport(data)
        this.childReports.ensureOnlineFromStatusReport()
      } else if (conProtocol === 2) {
        this.childReports.disconnectCurrentMouse()
      }
      return
    }

    void this.handleBaseReport(data)
  }

  /** 根据首字节命令分发配对、断连和子鼠标状态上报。 */
  private async handleBaseReport(data: DataView): Promise<void> {
    const cmd = data.getUint8(0)
    console.log(`[NordicNrf54LBaseDevice] Received report: ${cmd.toString(16)}`)
    switch (cmd) {
      case 0xb1:
        // 配对成功：父设备会让子设备初始化配置，需清除缓存确保后续读取从硬件获取最新数据。
        this.childReports.clearCurrentChildCache()
        // 再通过透传命令读取子鼠标 VID/PID，然后抛给上层测试页。
        await this.childReports.emitCurrentConnectedMouse()
        break
      // B2不查原因是，子鼠标关了开关，放到底座上，底座依然会上报B2
      // case 0xb2:
      //   // 子鼠标连接
      //   this.childReports.emitCurrentConnectedMouse()
      //   break
      case 0xb3:
        // 子鼠标断连。
        this.childReports.disconnectCurrentMouse()
        break
      case 0xb0:
        // 子鼠标状态主动上报：连接方式、DPI、电量、回报率、配置等。
        this.childReports.emitReport(data)
        // b0 说明链路可用。不能用 hasDetectedChild() 做门闩：
        // 0xb3 离线后身份仍保留，否则会挡住重新上线；无条件查询又会在高频 b0 下刷屏。
        // ensureOnlineFromStatusReport：已在线跳过；已识别则直接恢复在线；未识别才透传查一次。
        this.childReports.ensureOnlineFromStatusReport()
        break
      default:
        this.emit('report', 0x07, new Uint8Array(data.buffer, data.byteOffset, data.byteLength))
        break
    }
  }

  // 暂时废弃：因为轮询读取而不依靠主动上报，可能会影响到固件更新时nordic升级------------------------
  /**
   * 启动子鼠标自适应轮询，补齐没有主动上报时的状态。
   *
   * 策略：
   * - 无子设备在线 → 每 1.5 秒快速探测，尽快发现用户接入鼠标。
   * - 有子设备在线 → 每 10 秒慢速心跳，保持对子设备状态的感知，
   *   同时大幅降低 0xa5 0xa4 透传命令的发送频率。
   * - 底座断连时销毁定时器。
   */
  // private startChildPolling(): void {
  //   if (this.childPollTimer) return

  //   // 连接时立即探测一次，覆盖已有配对但无主动上报的场景。
  //   void this.childReports.emitCurrentConnectedMouse()

  //   const poll = () => {
  //     this.childPollTimer = setTimeout(
  //       () => {
  //         void this.childReports.emitCurrentConnectedMouse()
  //         poll()
  //       },
  //       this.childReports.hasOnlineChild() ? 10000 : 1500,
  //     )
  //   }
  //   poll()
  // }

  /** 停止自适应轮询定时器。 */
  // private stopChildPolling(): void {
  //   if (this.childPollTimer) {
  //     clearTimeout(this.childPollTimer)
  //     this.childPollTimer = null
  //   }
  // }

  // 暂时废弃：因为轮询读取而不依靠主动上报，可能会影响到固件更新时nordic升级------------------------

  /**
   * 打开屏幕素材上传使用的辅助 HID 通道。
   *
   * ESPR8PRO/VPR8PLUS 图片数据使用 usage=1，普通命令使用 usage=14；
   * 这个能力目前属于 Nordic nRF54L 底座族，不放到底座公共 device 逻辑里。
   */
  private async createScreenUploadChannel(
    usagePage: number,
    usage: number,
  ): Promise<HidChannel | null> {
    return this.session.channels.openChannel({
      usagePage,
      usage,
    })
  }
}

/** 兼容旧导出名；manifest driver 字符串仍为 VT_nrf54L_Base。 */
export { NordicNrf54LBaseDevice as VTNrf54LBaseDevice }
